import { Autocomplete, styled } from '@mui/material';
import { AppTextInput } from 'components/FormElements';
import { forwardRef } from 'react';
import trpcApi from 'lib/trpcClient';
import { pluralize } from 'further-ui/utils';

type Props = {
  error?: {
    message?: string;
  };
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  name?: string;
  disabled?: boolean;
  firmId: string;
  excludeEmails: Array<string>;
};

const LinkedAccountText = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(12),
  textAlign: 'right',
}));

const FirmAssociatedEmailsSelector = forwardRef<HTMLDivElement, Props>(
  (
    {
      name,
      disabled,
      error,
      placeholder,
      value = '',
      onChange,
      firmId,
      excludeEmails,
    },
    ref,
  ) => {
    const { data: furtherUserEmails } =
      trpcApi.investorAccessGrant.getAllFurtherUserEmailsAssociatedWithFirmInvestors.useQuery(
        { firmId },
      );

    const previouslyUsedEmailsForInvestors =
      furtherUserEmails?.filter(
        (fUser) => !excludeEmails.includes(fUser?.email ?? ''),
      ) ?? [];

    return (
      <Autocomplete
        freeSolo
        clearOnBlur
        handleHomeEndKeys
        selectOnFocus
        disabled={disabled}
        ref={ref}
        options={previouslyUsedEmailsForInvestors}
        getOptionKey={(option) =>
          typeof option === 'string' ? option : option.email ?? ''
        }
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.email
        }
        renderOption={(props, option) => {
          const investorsLabel = pluralize(
            'investor',
            option.linkedInvestors?.length,
          );
          const linkedInvestorNames = option.linkedInvestors
            ?.map((i) => i?.fullName)
            .join(', ');

          return (
            <li
              {...props}
              key={option.email}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <span>{option.email}</span>
              {option.linkedInvestors?.length > 0 ? (
                <LinkedAccountText>
                  {`(linked with ${investorsLabel} ${linkedInvestorNames})`}
                </LinkedAccountText>
              ) : option.linkedAdviser ? (
                <LinkedAccountText>
                  {`(linked to adviser ${option.linkedAdviser?.fullName})`}
                </LinkedAccountText>
              ) : option.linkedOrgUser ? (
                <LinkedAccountText>
                  {`(linked to organisation user ${option.linkedOrgUser?.fullName})`}
                </LinkedAccountText>
              ) : null}
            </li>
          );
        }}
        value={value}
        renderInput={(params) => (
          <AppTextInput
            {...params}
            name={name}
            error={!!error}
            helperText={error?.message?.toString()}
            placeholder={placeholder}
          />
        )}
        onInputChange={(_, newInputValue) => {
          onChange(newInputValue);
        }}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            onChange(newValue);
          } else if (newValue?.email) {
            onChange(newValue.email);
          } else {
            onChange('');
          }
        }}
      />
    );
  },
);

export default FirmAssociatedEmailsSelector;
